import React, { useEffect, useMemo, useState } from 'react';
import { Edit, RefreshCcw, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import OverridesPanel from '../../../components/rbac/users/OverridesPanel';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  benutzerName,
  standortUsersApi,
  type Katalog,
  type RbacBenutzer,
  type StandortOption,
} from '../../../lib/rbac/userAdmin';

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

const BR = P.standort.benutzer_und_zugriffsrechte;

interface StandortStand {
  team: boolean;
  standortrollen: string[];
  teamrollen: string[];
}

const standDes = (user: RbacBenutzer, standortId: number, katalog: Katalog): StandortStand => {
  const teamRole = new Set(katalog.rollen.filter((r) => r.team_rolle).map((r) => r.key));
  return {
    team: user.standort_team_ids.includes(standortId),
    standortrollen: user.rollen
      .filter((r) => r.scope_type === 'location' && r.standort_ids.includes(standortId) && !teamRole.has(r.role_key))
      .map((r) => r.role_key),
    teamrollen: Array.from(new Set(user.rollen.filter((r) => teamRole.has(r.role_key)).map((r) => r.role_key))),
  };
};

/** Benutzer der eigenen Standorte (RBAC-SPEC 10.6) — keine globale Benutzerverwaltung. */
const StandortUserManagement = () => {
  const { can } = usePermissions();
  const [katalog, setKatalog] = useState<Katalog | null>(null);
  const [users, setUsers] = useState<RbacBenutzer[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addStandort, setAddStandort] = useState('');
  const [selected, setSelected] = useState<RbacBenutzer | null>(null);
  const [stand, setStand] = useState<Record<number, StandortStand>>({});
  const [saving, setSaving] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, list] = await Promise.all([standortUsersApi.katalog(), standortUsersApi.list()]);
      setKatalog(k);
      setUsers(list);
    } catch (e) {
      toast.error(errorText(e, 'Benutzer konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const standorte = useMemo(() => katalog?.standorte ?? [], [katalog]);
  const standortName = useMemo(() => new Map(standorte.map((s) => [s.id, s.name])), [standorte]);
  const roleLabel = useMemo(() => new Map((katalog?.rollen ?? []).map((r) => [r.key, r.label])), [katalog]);
  const addStandorte = standorte.filter((s) => can(BR.standortbenutzer_hinzufuegen, { standortId: s.id }));
  const overrideStandorte = standorte
    .filter((s) => can(BR.standortbezogene_berechtigungen_bearbeiten, { standortId: s.id }))
    .map((s) => s.id);

  const openEdit = (user: RbacBenutzer) => {
    if (!katalog) return;
    setSelected(user);
    const next: Record<number, StandortStand> = {};
    for (const id of user.standort_ids) next[id] = standDes(user, id, katalog);
    setStand(next);
  };

  const replaceUser = (next: RbacBenutzer) => {
    setUsers((prev) => prev.map((u) => (u.account_id === next.account_id ? next : u)));
    openEdit(next);
  };

  const addUser = async () => {
    if (!addEmail.trim() || !addStandort) {
      toast.error('Bitte E-Mail und Standort angeben');
      return;
    }
    try {
      const added = await standortUsersApi.addZugang(addEmail.trim(), Number(addStandort));
      setUsers((prev) => [...prev.filter((u) => u.account_id !== added.account_id), added]);
      setAddOpen(false);
      setAddEmail('');
      toast.success('Benutzer dem Standort zugeordnet');
    } catch (e) {
      toast.error(errorText(e, 'Zuordnen fehlgeschlagen'));
    }
  };

  const saveStandort = async (standortId: number) => {
    if (!selected || !katalog) return;
    const wanted = stand[standortId];
    const current = standDes(selected, standortId, katalog);
    const teamChanged =
      [...wanted.teamrollen].sort().join() !== [...current.teamrollen].sort().join();
    setSaving(standortId);
    try {
      replaceUser(
        await standortUsersApi.setRollen(selected.account_id, standortId, {
          standort_team: wanted.team,
          standortrollen: wanted.standortrollen,
          teamrollen: teamChanged ? wanted.teamrollen : null,
        }),
      );
      toast.success(`Rollen an ${standortName.get(standortId)} gespeichert`);
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(null);
    }
  };

  const zugangUmschalten = async (standortId: number, aktiv: boolean) => {
    if (!selected) return;
    try {
      replaceUser(await standortUsersApi.setZugangAktiv(selected.account_id, standortId, aktiv));
      toast.success(aktiv ? 'Zugang freigegeben' : 'Zugang gesperrt');
    } catch (e) {
      toast.error(errorText(e, 'Zugang konnte nicht geändert werden'));
    }
  };

  const removeZugang = async (standortId: number) => {
    if (!selected || !katalog) return;
    if (!confirm(`Zugang von ${benutzerName(selected)} zu ${standortName.get(standortId)} entfernen? Rollen und Team an diesem Standort entfallen.`)) return;
    try {
      const current = standDes(selected, standortId, katalog);
      if (current.team || current.standortrollen.length > 0) {
        await standortUsersApi.setRollen(selected.account_id, standortId, {
          standort_team: false,
          standortrollen: [],
          teamrollen: null,
        });
      }
      const next = await standortUsersApi.removeZugang(selected.account_id, standortId);
      if (next.standort_ids.length === 0) {
        setUsers((prev) => prev.filter((u) => u.account_id !== next.account_id));
        setSelected(null);
      } else {
        replaceUser(next);
      }
      toast.success('Zugang entfernt');
    } catch (e) {
      toast.error(errorText(e, 'Zugang konnte nicht entfernt werden'));
      await load();
    }
  };

  const setStandFor = (standortId: number, patch: Partial<StandortStand>) =>
    setStand((prev) => ({ ...prev, [standortId]: { ...prev[standortId], ...patch } }));

  const roleCheckbox = (s: StandortOption, key: string, field: 'standortrollen' | 'teamrollen') => {
    const current = stand[s.id]?.[field] ?? [];
    const vergebbar = (s.vergebbare_rollen ?? []).includes(key);
    return (
      <label key={key} className={`flex items-center gap-2 text-sm ${vergebbar ? '' : 'opacity-60'}`}
        title={vergebbar ? undefined : 'Diese Rolle vergibt die globale Benutzerverwaltung'}>
        <Checkbox
          checked={current.includes(key)}
          disabled={!vergebbar}
          onCheckedChange={(c) =>
            setStandFor(s.id, {
              [field]: c === true ? [...current.filter((k) => k !== key), key] : current.filter((k) => k !== key),
            })
          }
        />
        {roleLabel.get(key) ?? key}
      </label>
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Standortbenutzer</h1>
          <p className="text-muted-foreground">Benutzer mit Zugang zu Ihren Standorten, ihre Rollen und Rechte dort</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCcw className="mr-2 h-4 w-4" /> Aktualisieren</Button>
          {addStandorte.length > 0 && (
            <Button onClick={() => { setAddStandort(String(addStandorte[0].id)); setAddOpen(true); }}>
              <UserPlus className="mr-2 h-4 w-4" /> Benutzer zuordnen
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Lade Benutzer…</h2>
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-2 md:p-3">
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead density="compact">Name</TableHead>
                <TableHead density="compact">Standorte</TableHead>
                <TableHead density="compact">Rollen</TableHead>
                <TableHead density="compact" className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && (
                <TableRow><TableCell density="compact" colSpan={4} className="text-muted-foreground">Keine Benutzer an Ihren Standorten.</TableCell></TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.account_id} className={u.aktiv ? '' : 'opacity-60'}>
                  <TableCell density="compact">
                    <div className="font-medium">{benutzerName(u)}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </TableCell>
                  <TableCell density="compact" className="text-sm">
                    {u.standort_ids.map((id) => standortName.get(id) ?? id).join(', ')}
                  </TableCell>
                  <TableCell density="compact">
                    <div className="flex flex-wrap gap-1">
                      {u.rollen.map((r) => (
                        <Badge key={r.id} size="sm" variant="info">
                          {roleLabel.get(r.role_key) ?? r.role_key}
                          {r.scope_type === 'location' ? ` · ${r.standort_ids.map((id) => standortName.get(id) ?? id).join(', ')}` : ''}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell density="compact" className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openEdit(u)}><Edit className="mr-2 h-4 w-4" /> Bearbeiten</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Benutzer zuordnen</DialogTitle>
            <DialogDescription>Bestehendes Mitarbeiterkonto einem Ihrer Standorte zuordnen. Neue Konten legt die globale Benutzerverwaltung an.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div><Label>E-Mail</Label><Input uiSize="md" type="email" value={addEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddEmail(e.target.value)} /></div>
            <div>
              <Label>Standort</Label>
              <Select value={addStandort} onValueChange={setAddStandort}>
                <SelectTrigger size="md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {addStandorte.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Abbrechen</Button>
            <Button onClick={addUser}>Zuordnen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={selected !== null} onOpenChange={(open: boolean) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {selected && katalog && (
            <>
              <DialogHeader>
                <DialogTitle>{benutzerName(selected)}</DialogTitle>
                <DialogDescription>{selected.email}</DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="rollen">
                <TabsList>
                  <TabsTrigger value="rollen">Rollen je Standort</TabsTrigger>
                  {overrideStandorte.length > 0 && <TabsTrigger value="rechte">Individuelle Rechte</TabsTrigger>}
                </TabsList>
                <TabsContent value="rollen" className="space-y-4 pt-4">
                  <p className="text-xs text-muted-foreground">
                    Teamrollen wirken über alle Standort-Teams des Benutzers. Ausgegraute Rollen vergibt die globale Benutzerverwaltung.
                  </p>
                  {standorte.filter((s) => selected.standort_ids.includes(s.id)).map((s) => {
                    const canAssign = can(BR.standortbezogene_rolle_vergeben, { standortId: s.id });
                    const canRemove = can(BR.standortbenutzer_entfernen, { standortId: s.id });
                    const canSperren = can(BR.standortbenutzer_deaktivieren, { standortId: s.id });
                    // Gesperrt: Rollen bleiben stehen, wirken an diesem Standort aber nicht (F27).
                    const gesperrt = selected.gesperrte_standort_ids.includes(s.id);
                    return (
                      <div key={s.id} className="space-y-3 rounded-md border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{s.name}</span>
                            {gesperrt && <Badge size="sm" variant="destructive">Zugang gesperrt</Badge>}
                          </div>
                          <div className="flex gap-2">
                            {canSperren && (
                              <Button variant="outline" size="sm" onClick={() => zugangUmschalten(s.id, gesperrt)}>
                                {gesperrt ? 'Freigeben' : 'Sperren'}
                              </Button>
                            )}
                            {canRemove && <Button variant="outline" size="sm" onClick={() => removeZugang(s.id)}>Zugang entfernen</Button>}
                          </div>
                        </div>
                        {canAssign && stand[s.id] && (
                          <>
                            <label className="flex items-center gap-2 text-sm">
                              <Checkbox checked={stand[s.id].team} onCheckedChange={(c) => setStandFor(s.id, { team: c === true })} />
                              Mitglied im Standort-Team
                            </label>
                            <div>
                              <div className="mb-1 text-xs font-medium text-muted-foreground">Teamrollen</div>
                              <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {katalog.rollen.filter((r) => r.team_rolle).map((r) => roleCheckbox(s, r.key, 'teamrollen'))}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-xs font-medium text-muted-foreground">Standortrollen</div>
                              <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {katalog.rollen.filter((r) => !r.team_rolle).map((r) => roleCheckbox(s, r.key, 'standortrollen'))}
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <Button size="sm" onClick={() => saveStandort(s.id)} disabled={saving === s.id}>Speichern</Button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </TabsContent>
                {overrideStandorte.length > 0 && (
                  <TabsContent value="rechte" className="pt-4">
                    <OverridesPanel mode="standort" accountId={selected.account_id} katalog={katalog}
                      standortIds={overrideStandorte.filter((id) => selected.standort_ids.includes(id))} />
                  </TabsContent>
                )}
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandortUserManagement;
